import { isArray } from '@dimjs/lang';
import { classNames } from '@dimjs/utils';
import { hooks } from '@wove/react';
import { useKeenSlider } from 'keen-slider/react';
import { forwardRef, ReactNode, useImperativeHandle, useState } from 'react';
import { useMutationEffect } from '../hooks/use-mutation-effect';
import { CommonPropsWithChildren, withNativeProps } from '../utils/native-props';
import { KeenSliderInstance, KeenSliderOptions, KeenSliderPlugin } from './keen-slider/keen-slider';
import './keen-slider/keen-slider.css';
import { swiperAutoSwitchPlugin } from './plugin/auto-switch';
import './style.less';

export type SwiperProps = {
  option?: { autoPlay?: boolean | { duration: number } } & KeenSliderOptions;
  plugins?: KeenSliderPlugin[];
  dot?: false | ((slider: KeenSliderInstance) => ReactNode);
} & CommonPropsWithChildren;

export type SwiperRefApi = {
  onNext: () => void;
  onPrev: () => void;
  moveToIdx: (idx: number, animation?: boolean) => void;
};

/**
 * 滑块组件
 * ```
 * 1. 当swiper处于modal弹窗中
 * 由于swiper渲染受到modal弹窗缩放过程影响，导致swiper宽度计算错误，所以要在onAfterOpen后渲染swiper组件
 * 例如：ImageViewer组件
 *
 * 2. 获取Swiper实例，可通过option中created生命周期
 *
 * 文档：
 * https://keen-slider.io/docs
 * https://keen-slider.io/examples
 * ```
 */
export const Swiper = forwardRef<SwiperRefApi, SwiperProps>((props, ref) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [instance, setInstance] = useState<any>();
  const childrens = isArray(props.children)
    ? props.children.filter(Boolean)
    : [props.children].filter(Boolean);

  const getOption = hooks.useCallbackRef(() => {
    const option: SwiperProps['option'] = {
      initial: 0,
      ...props.option,
      slideChanged(slider) {
        setCurrentSlide(slider.track.details.rel);
        props.option?.slideChanged?.(slider);
      },
      created(slider) {
        setInstance(slider);
        props.option?.created?.(slider);
      },
    };

    if (childrens.length <= 1) {
      option.loop = false;
    }
    if (childrens.length <= 1) {
      option.autoPlay = false;
    }
    return option;
  });

  const option = getOption();

  const getPlugins = hooks.useCallbackRef(() => {
    const plugins =
      option.autoPlay && childrens.length > 1
        ? [swiperAutoSwitchPlugin(option.autoPlay === true ? undefined : option.autoPlay)].concat(
            props.plugins || [],
          )
        : props.plugins;
    return plugins || [];
  });

  const [sliderRef] = useKeenSlider<HTMLDivElement>(option as any, getPlugins() as any);

  /**
   * 监听 Swiper DOM树变化
   * 例如：Swiper中滑块的增删改查
   * @returns
   */
  useMutationEffect(
    () => {
      instance?.update();
    },
    instance?.container,
    { childList: true },
  );

  const dotRender = props.dot !== false && childrens.length > 1 && (
    <div className="v-swiper-dot">
      {childrens.map((_, index) => {
        return (
          <div
            key={index}
            className={classNames('v-swiper-dot-item', {
              'v-swiper-dot-item-active': currentSlide === index,
            })}
            onClick={(event) => {
              event.stopPropagation();
              instance?.moveToIdx(index);
            }}
          ></div>
        );
      })}
    </div>
  );

  useImperativeHandle(ref, () => {
    return {
      onPrev: () => {
        instance?.prev();
      },
      onNext: () => {
        instance?.next();
      },
      moveToIdx: (idx, animation) => {
        instance?.moveToIdx(idx, false, animation === false ? { duration: 0 } : undefined);
      },
      instanceRef: () => {
        return instance;
      },
    };
  });

  return withNativeProps(
    props,
    <div className="v-swiper">
      <div ref={sliderRef} className="keen-slider">
        {props.children}
      </div>
      {typeof props.dot === 'function' ? props.dot(instance) : dotRender}
    </div>,
  );
});
